<template>
    <div class="modal-container">
        <div class="modal-header">
            <span v-if="!isEdit">Add Team Member Custom Field</span>
            <span v-else>Edit {{ customField.label }}</span>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.localModel" class="form-wrapper">
                <form-group v-if="!isEdit">
                    <md-select v-model="selectedFieldGroup" @md-selected="createFieldGroup">
                        <md-option value="">Please select</md-option>
                        <md-option v-for="grp in sortedFieldGroupOptions" :value="grp.value" :key="grp.value">
                            {{ grp.label }}
                        </md-option>
                    </md-select>
                </form-group>

                <template v-if="showFields">
                    <form-group v-if="!isStandaloneField" label="Group Label" name="groupLabel">
                        <md-input v-model="localModel.label" />
                    </form-group>
                    <fieldset>
                        <div v-for="(fld, ndx) in localModel.fields" :key="`field-${ndx}`" class="field-group">
                            <form-group v-show="isEdit" label="Field Name" name="fieldName">
                                <md-input v-model="fld.name" disabled />
                            </form-group>
                            <form-group label="Field Label" name="fieldLabel">
                                <md-input v-model="fld.label" />
                            </form-group>
                            <form-group name="type">
                                <vue-select
                                    :disabled="!isStandaloneField"
                                    v-if="customFieldTypes"
                                    :options="customFieldTypes"
                                    placeholder="Type"
                                    v-model="fld.type"
                                    :searchable="$root.isDesktop"
                                ></vue-select>
                            </form-group>
                        </div>
                    </fieldset>
                </template>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" @click="validate">
                {{ isEdit ? 'Save' : 'Add' }}
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">
                Cancel
            </md-button>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { CUSTOM_FIELD_TYPES_CONSTANTS } from '@/utils/constants';
import { fieldGroupOptions, STANDALONE_FIELDS_GROUP } from '@/utils/customFields';

export default {
    name: 'TeamMemberCustomFieldModal',
    mixins: [GeneralMixin],
    props: {
        isEdit: {
            type: Boolean,
            default: () => false
        },
        customField: {
            type: Object,
            default: () => null
        },
        checkDuplicateGroup: {
            type: Function,
            default: () => {}
        },
        getFieldGroupOptions: {
            type: Function,
            default: () => null
        },
        hasDuplicateNameInStandaloneFields: {
            type: Function,
            default: () => null
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            selectedFieldGroup: '',
            showFields: false,
            localModel: {
                label: null,
                group: null,
                fields: [
                    {
                        name: null,
                        label: null,
                        type: null
                    },
                    {
                        name: null,
                        label: null,
                        type: null
                    }
                ]
            },
            customFieldTypes: CUSTOM_FIELD_TYPES_CONSTANTS
        };
    },
    computed: {
        sortedFieldGroupOptions() {
            let allFields = [];
            if (this.getFieldGroupOptions) {
                allFields = allFields.concat(this.getFieldGroupOptions());
            }
            allFields.push({
                label: 'Standalone Field',
                value: STANDALONE_FIELDS_GROUP
            });

            return allFields;
        },
        isStandaloneField() {
            return this.selectedFieldGroup === STANDALONE_FIELDS_GROUP;
        }
    },
    validations: {
        localModel: {
            label: {
                required
            },
            group: {
                required
            },
            fields: {
                $each: {
                    name: {
                        required
                    },
                    label: {
                        required
                    },
                    type: {
                        required
                    }
                }
            }
        }
    },
    methods: {
        createFieldGroup(value) {
            this.localModel.fields = [];
            if (!value) {
                return;
            }

            if (value === STANDALONE_FIELDS_GROUP) {
                this.localModel = {
                    label: 'Standalone',
                    group: value,
                    fields: [
                        {
                            name: null,
                            label: null,
                            type: null
                        }
                    ]
                };
                this.showFields = true;
                return;
            }

            if (this.checkDuplicateGroup(value)) {
                return;
            }

            const { label } = fieldGroupOptions.find((x) => x.value === value);

            // right now only 2 pair fields are required (document number and expiry date)
            // but in the future, this can be changed to a dynamic number of fields
            // and this can be moved to a separate helper function to load the applicable fields
            // based on the selected group
            this.localModel = {
                label,
                group: value,
                fields: [
                    {
                        name: 'documentNumber',
                        label: 'Document Number',
                        type: 'Text'
                    },
                    {
                        name: 'expiryDate',
                        label: 'Expiry Date',
                        type: 'Date'
                    }
                ]
            };

            this.showFields = true;
        },
        async validate() {
            if (this.isStandaloneField && !this.isEdit) {
                // populate 'name' based on 'label'
                this.localModel.fields[0].name = this.localModel.fields[0].label.toLowerCase().replace(/[^\w]/gi, '');
            }

            if (this.isStandaloneField && this.hasDuplicateNameInStandaloneFields(this.localModel.fields[0].name)) {
                showErrorMessage(this, 'Custom field with this name already exists.');
                return;
            }

            this.$v.localModel.$touch();
            if (this.$v.localModel.$invalid) {
                showErrorMessage(this, 'Please fill in all required fields.');
                return;
            }

            await this.saveCustomFields();
        },
        async saveCustomFields() {
            this.$_handleLoaderState(true, 'Saving custom fields...');
            const payload = {
                method: 'post',
                data: this.localModel
            };
            const api = `/api/teams/custom-fields/team-members`;

            try {
                await handleRequests(api, payload);

                this.$notify({
                    message: `Custom fields saved!`,
                    type: 'success'
                });

                await this.$store.dispatch('user/FETCH_USER');

                this.resolve(this.localModel);
            } catch (e) {
                const message = 'Cannot save custom fields.';
                showErrorMessage(this, message, e);

                this.resolve(null);
            } finally {
                this.$_handleLoaderState(false);
            }
        }
    },
    mounted() {
        if (this.isEdit) {
            this.localModel = JSON.parse(JSON.stringify(this.customField));

            this.selectedFieldGroup = this.customField.group;
            this.showFields = true;
        }
    }
};
</script>

<style lang="scss" scoped>
fieldset {
    border: none;
}

::v-deep .field-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.modal-container {
    max-height: 80vh;
    overflow-y: auto;
}
</style>
